import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import Editor, { OnMount } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import { toast } from 'material-react-toastify';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useStyleEditor } from './StyleEditorContext';
import { useEditableComponentStyle } from './utils';
import { ComponentStyle } from '../../../interfaces/Settings/SettingsV2/ComponentStyle';
import { sharedStyles } from '../../../utils/Style';
import { Waiting } from '../../../components/Waiting/Waiting';
import { generateStyles } from '../../../api/Settings';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isValidComponentStyle = (value: unknown): value is ComponentStyle => {
  if (!isPlainObject(value)) {
    return false;
  }

  return Object.values(value).every(
    (item) =>
      typeof item === 'string' ||
      typeof item === 'number' ||
      isPlainObject(item),
  );
};

export const StyleEditorDrawer = () => {
  const { isOpen, closeDrawer, openedPath } = useStyleEditor();
  const { value, setValue } = useEditableComponentStyle(openedPath);

  const originalStyleRef = useRef<ComponentStyle | null>(null);

  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);

  const initialJsonText = useMemo(
    () => JSON.stringify(value ?? {}, null, 2),
    [openedPath, isOpen],
  );

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [userRequest, setUserRequest] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setJsonText(initialJsonText);
    setUserRequest('');
    setError(null);

    // store original style snapshot
    originalStyleRef.current = value ?? {};
  }, [initialJsonText, openedPath]);

  const handleGenerate = () => {
    setGenerating(true);
    generateStyles(userRequest, jsonText)
      .then((text) => {
        setJsonText(text);

        let parsed;

        try {
          parsed = JSON.parse(text);
        } catch {
          return;
        }

        if (!isValidComponentStyle(parsed)) {
          return;
        }

        // live preview
        setValue(parsed, false);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setGenerating(false));
  };

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      trailingCommas: 'error',
      schemaValidation: 'error',
      enableSchemaRequest: false,
    });

    editor.onDidBlurEditorText(() => {
      const text = editor.getValue();

      let parsed;

      try {
        parsed = JSON.parse(text);
      } catch {
        return;
      }

      if (!isValidComponentStyle(parsed)) {
        return;
      }

      // live preview
      setValue(parsed, false);
    });
  };

  const handleCancel = () => {
    if (originalStyleRef.current) {
      setValue(originalStyleRef.current, false);
    }

    closeDrawer();
  };

  const handleSave = () => {
    let parsed;

    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setError('Geçersiz JSON');
      return;
    }

    if (!isValidComponentStyle(parsed)) {
      setError('Geçersiz değer');
      return;
    }

    setLoading(true);
    setValue(parsed, true)
      .then(() => {
        toast.success('Değişiklikler başarıyla kaydedildi.');
        closeDrawer();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Drawer anchor='right' open={isOpen} onClose={handleCancel}>
      <Waiting open={loading} />

      <Box
        sx={{
          width: 700,
          maxWidth: '100vw',
          height: '100vh',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant='h6'>Görünümü Düzenle</Typography>
          <Typography variant='body2' color='text.secondary'>
            {openedPath}
          </Typography>
          {!!error && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Editor
            height='100%'
            defaultLanguage='json'
            language='json'
            value={jsonText}
            onMount={handleEditorMount}
            onChange={(x) => {
              setJsonText(x ?? '');
              if (error) {
                setError(null);
              }
            }}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              wordWrap: 'on',
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: true,
              scrollBeyondLastLine: false,
              tabSize: 2,
            }}
          />
        </Box>

        <TextField
          label='İstenen değişiklikler'
          placeholder='Örnek: Yazı fontu italik olsun, font 12 piksel olsun, arka plan mavi olsun'
          variant='outlined'
          size='small'
          multiline
          minRows={4}
          disabled={generating}
          value={userRequest}
          onChange={(e) => setUserRequest(e.target.value)}
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton
                color='primary'
                disabled={generating}
                onClick={handleGenerate}
              >
                <AutoAwesomeIcon />
              </IconButton>
            ),
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            justifyContent: 'flex-end',
            mt: 'auto',
          }}
        >
          <Button
            variant='outlined'
            onClick={handleCancel}
            sx={{ ...sharedStyles.buttonText }}
          >
            İptal
          </Button>

          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
